import React, { useCallback, useEffect, useState } from "react";
import pinPrompt from "../utils/pinPrompt";
import {
  API_BASE, apiJson, fmtDate, fmtBytes, openCertificateFile,
  CUSTOMER_STATUS, ROW_STATUS, TAX_TREATMENTS,
} from "../utils/taxCertificates";
import { loadTaxCertificates } from "../features/customers/customerData";
import TaxCertRequestModal from "./TaxCertRequestModal";
import TaxCertRecordModal from "./TaxCertRecordModal";

/**
 * "Sales tax exemption" section on the customer page.
 *
 * One glance answers Annie's filing question — is there a signed Kansas
 * exemption certificate on file for this customer? — and the buttons cover
 * the whole loop: request it by email (portal upload link), record one that
 * came back by email/paper, verify / reject / revoke, re-send an open
 * request. `tax_treatment` marks customers we charge tax to, so they stop
 * showing as "missing" on the monthly report.
 *
 * Props: customerId, customer {name, contact_name, email, state}, contacts[]
 */
export default function TaxCertificatesCard({ customerId, customer, contacts = [] }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [recordFor, setRecordFor] = useState(null);   // null | {certificateId, initial}
  const [showAll, setShowAll] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setErr(null);
    try {
      setData(await loadTaxCertificates(customerId)); // features/customers/customerData (the phone's read too)
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  async function setTreatment(value) {
    try {
      await apiJson(`${API_BASE}/customers/${customerId}`, { method: "PATCH", body: JSON.stringify({ tax_treatment: value }) });
      await load();
    } catch (e) {
      alert(`Could not update: ${e.message || e}`);
    }
  }

  async function act(cert, action) {
    let reason = null;
    if (action === "reject") {
      reason = window.prompt("Why is this certificate being rejected? (sent nothing to the customer yet — you can request a new one after)");
      if (!reason || !reason.trim()) return;
    }
    const confirmText = {
      verify: "Mark this certificate as verified and on file?",
      revoke: "Revoke this verified certificate? The customer will show as needing one again.",
      cancel: "Cancel this request? The emailed upload link stops working.",
    }[action];
    if (confirmText && !window.confirm(confirmText)) return;
    const pin = await pinPrompt(`Enter your PIN to ${action} this certificate:`);
    if (!pin) return;
    try {
      const r = await apiJson(`${API_BASE}/customers/${customerId}/tax_certificates/${cert.certificate_id}`, {
        method: "PATCH", body: JSON.stringify({ pin, action, rejected_reason: reason }),
      });
      setFlash(r.message);
      await load();
    } catch (e) {
      alert(String(e.message || e));
    }
  }

  async function resend(cert) {
    const to = window.prompt("Re-send the request (a fresh upload link) to:", cert.requested_to_email || customer?.email || "");
    if (!to) return;
    const pin = await pinPrompt("Enter your PIN to re-send the request:");
    if (!pin) return;
    try {
      const r = await apiJson(`${API_BASE}/customers/${customerId}/tax_certificates/${cert.certificate_id}/resend`, {
        method: "POST", body: JSON.stringify({ pin, to_email: to.trim() }),
      });
      setFlash(r.message);
      await load();
    } catch (e) {
      alert(String(e.message || e));
    }
  }

  async function viewFile(cert) {
    try { await openCertificateFile(customerId, cert.certificate_id); } catch (e) { alert(String(e.message || e)); }
  }

  const status = data?.certificate_status;
  const meta = CUSTOMER_STATUS[status] || { label: status || "…", badge: "bg-secondary" };
  const rows = data?.certificates || [];
  const visible = showAll ? rows : rows.filter((r) => !["cancelled", "revoked", "rejected"].includes(r.status)).slice(0, 5);
  const hiddenCount = rows.length - visible.length;
  const custForModal = { customer_id: customerId, ...(data?.customer || {}), ...(customer || {}) };

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <h5 className="mb-0">Sales Tax Exemption</h5>
          {data && <span className={`badge ${meta.badge}`}>{meta.label}</span>}
          {loading && <span className="text-muted small">loading…</span>}
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-primary" onClick={() => setShowRequest(true)} disabled={!data}>
            ✉️ Request by email
          </button>
          <button className="btn btn-sm btn-outline-success" onClick={() => setRecordFor({ certificateId: null, initial: {} })} disabled={!data}>
            ⬆️ Record received certificate
          </button>
        </div>
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}
      {flash && <div className="alert alert-success py-2 d-flex justify-content-between"><span>{flash}</span><button className="btn-close" onClick={() => setFlash(null)} /></div>}

      {data && (
        <div className="row g-3 mb-2">
          <div className="col-md-5">
            <label className="form-label mb-1">How we bill this customer</label>
            <select className="form-select form-select-sm" value={data.customer?.tax_treatment || "unknown"} onChange={(e) => setTreatment(e.target.value)}>
              {TAX_TREATMENTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div className="form-text">
              Exempt customers must have a signed Kansas certificate on file (ST-28D ingredient/component part, ST-28A resale, ST-28M multi-jurisdiction). Taxable customers don't need one and drop off the monthly report.
            </div>
          </div>
          <div className="col-md-7">
            {data.certificate ? (
              <div className="small">
                {data.certificate.form_type === "UNKNOWN"
                  ? <div><strong>Requested:</strong> {(data.certificate.requested_form_types || []).join(" or ") || "—"} <span className="text-muted">(the customer picks the form that fits)</span></div>
                  : <div><strong>{data.certificate.form_type}</strong>{data.certificate.form_title ? ` — ${data.certificate.form_title}` : ""}</div>}
                {data.certificate.status === "verified" && <div>Verified {fmtDate(data.certificate.verified_at)} by {data.certificate.verified_by}{data.certificate.signed_on ? ` · signed ${fmtDate(data.certificate.signed_on)}` : ""}{data.certificate.expires_on ? ` · expires ${fmtDate(data.certificate.expires_on)}` : " · no expiry"}</div>}
                {data.certificate.status === "received" && <div>Uploaded {fmtDate(data.certificate.uploaded_at)} via {data.certificate.upload_source === "customer_portal" ? "the customer upload link" : data.certificate.uploaded_by} — review it below.</div>}
                {data.certificate.status === "requested" && <div>Requested {fmtDate(data.certificate.requested_at)} by {data.certificate.requested_by} → {data.certificate.requested_to_email} ({(data.certificate.requested_form_types || []).join(" / ")}). Link valid until {fmtDate(data.certificate.request_token_expires_at)}.</div>}
                {data.certificate.tax_id && <div>Tax # {data.certificate.tax_id}{data.certificate.tax_id_state ? ` (${data.certificate.tax_id_state})` : ""}</div>}
                {data.customer?.tax_treatment === "taxable" && <div className="text-warning">Marked taxable — invoices for this customer should carry sales tax.</div>}
              </div>
            ) : (
              <div className="small text-muted">
                {status === "not_required" ? "No certificate needed — we charge this customer sales tax." : "Nothing on file yet. Suggested form" + ((data.suggested_form_types || []).length > 1 ? "s" : "") + ": " + (data.suggested_form_types || []).join(" + ") + (data.is_kansas === false ? " (address is outside Kansas)" : data.is_kansas ? " (Kansas address)" : " (no state on file)") + "."}
              </div>
            )}
          </div>
        </div>
      )}

      {data && rows.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-1">
            <thead>
              <tr>
                <th>Status</th>
                <th>Form</th>
                <th>Signed</th>
                <th>Expires</th>
                <th>File</th>
                <th>History</th>
                <th className="text-end"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.certificate_id} className={c.status === "received" ? "table-info" : ""}>
                  <td><span className={`badge ${ROW_STATUS[c.status] || "bg-secondary"}`}>{c.status}</span>{c.is_expired && c.status === "verified" && <span className="badge bg-danger ms-1">expired</span>}</td>
                  <td>{c.form_type === "UNKNOWN" ? <span className="text-muted">{(c.requested_form_types || []).join(" / ") || "—"}</span> : c.form_type}</td>
                  <td>{fmtDate(c.signed_on)}</td>
                  <td>{c.expires_on ? fmtDate(c.expires_on) : <span className="text-muted">—</span>}</td>
                  <td>
                    {c.has_file
                      ? <button className="btn btn-link btn-sm p-0" onClick={() => viewFile(c)}>{c.file_name}{c.file_size_bytes ? <span className="text-muted"> ({fmtBytes(c.file_size_bytes)})</span> : null}</button>
                      : <span className="text-muted">none</span>}
                  </td>
                  <td className="small text-muted">
                    {c.status === "requested" && <>Sent {fmtDate(c.last_sent_at || c.requested_at)}{c.request_sent_count > 1 ? ` (×${c.request_sent_count})` : ""} to {c.requested_to_email}</>}
                    {c.status === "received" && <>Received {fmtDate(c.uploaded_at)} · {c.upload_source === "customer_portal" ? "customer upload" : c.uploaded_by}</>}
                    {c.status === "verified" && <>Verified {fmtDate(c.verified_at)} · {c.verified_by}</>}
                    {c.status === "rejected" && <>Rejected {fmtDate(c.rejected_at)}: {c.rejected_reason}</>}
                    {c.status === "revoked" && <>Revoked {fmtDate(c.closed_at)} · {c.closed_by}</>}
                    {c.status === "cancelled" && <>Cancelled {fmtDate(c.closed_at)}</>}
                    {c.notes && <div>{c.notes}</div>}
                  </td>
                  <td className="text-end text-nowrap">
                    {c.status === "requested" && <>
                      <button className="btn btn-sm btn-outline-success me-1" title="Attach the signed copy that came back by email" onClick={() => setRecordFor({ certificateId: c.certificate_id, initial: c })}>Attach file</button>
                      <button className="btn btn-sm btn-outline-secondary me-1" title="Re-sends the same forms with a fresh link. To offer different forms, send a new request — it replaces this one." onClick={() => resend(c)}>Re-send</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => act(c, "cancel")}>Cancel</button>
                    </>}
                    {c.status === "received" && <>
                      <button className="btn btn-sm btn-success me-1" onClick={() => act(c, "verify")}>Verify</button>
                      <button className="btn btn-sm btn-outline-secondary me-1" title="Fix the form type / dates before verifying" onClick={() => setRecordFor({ certificateId: c.certificate_id, initial: c })}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => act(c, "reject")}>Reject</button>
                    </>}
                    {c.status === "verified" && <>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => act(c, "revoke")}>Revoke</button>
                    </>}
                    {c.status === "rejected" && <>
                      <button className="btn btn-sm btn-outline-success" onClick={() => setRecordFor({ certificateId: c.certificate_id, initial: c })}>Re-file</button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(hiddenCount > 0 || showAll) && (
            <button className="btn btn-link btn-sm p-0" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Hide closed rows" : `Show ${hiddenCount} more (cancelled / rejected / revoked)`}
            </button>
          )}
        </div>
      )}

      {showRequest && (
        <TaxCertRequestModal
          customer={custForModal}
          contacts={contacts}
          suggestedFormTypes={data?.suggested_form_types}
          onClose={() => setShowRequest(false)}
          onSent={async (r) => { setShowRequest(false); setFlash(`${r.message} Upload link: ${r.portal_url}`); await load(); }}
        />
      )}
      {recordFor && (
        <TaxCertRecordModal
          customer={custForModal}
          certificateId={recordFor.certificateId}
          initial={recordFor.initial || {}}
          onClose={() => setRecordFor(null)}
          onSaved={async (r) => { setRecordFor(null); setFlash(r.message); await load(); }}
        />
      )}
    </div>
  );
}
